// src/business/layout/BusinessLayout.jsx
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Menu as MenuIcon } from "lucide-react";

import BusinessSidebar from "../components/BusinessSidebar";
import BusinessTopbar from "../components/BusinessTopbar";
import { useBusinessAuth } from "../context/BusinessAuthContext";
import { IconButton, Sheet } from "../../components/ui";
import "../../styles/operations-portal.css";
import "../business-ui.css";

/**
 * BusinessLayout — the Business Portal shell (DESIGN.md › Layout).
 * - Desktop (md+): 232 px sidebar, 52 px topbar, 24 px page gutter.
 * - Phones: 52 px topbar with the menu button; navigation opens in a sheet.
 * - Pages render inside <main>; the shell owns gutters and width, not pages.
 * - Keeps a permanent SweetAlert root in the DOM.
 * - Does NOT enforce permissions; the backend does (401/403).
 */
export default function BusinessLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef(null);
  const { currentBusiness } = useBusinessAuth();
  const { pathname } = useLocation();
  const isMessagingRoute = pathname.startsWith("/business/messages");
  const isSuspendedBusiness = String(currentBusiness?.status || "").toUpperCase() === "SUSPENDED";
  const shouldRedirectToTrust = isSuspendedBusiness && !pathname.startsWith("/business/settings");

  const scrollBusinessContentToTop = useCallback(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;

    const pageScroller = document.scrollingElement || document.documentElement;
    if (pageScroller) pageScroller.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, []);

  const handleSidebarNavigate = useCallback(() => {
    scrollBusinessContentToTop();
    setMobileNavOpen(false);
  }, [scrollBusinessContentToTop]);

  useEffect(() => {
    const id = "business-swal-root";
    let node = document.getElementById(id);

    if (!node) {
      node = document.createElement("div");
      node.id = id;
      document.body.appendChild(node);
    }

    // keep permanently (avoid fast-nav issues)
    return () => {};
  }, []);

  useEffect(() => {
    document.body.classList.add("business-portal-active");
    return () => document.body.classList.remove("business-portal-active");
  }, []);

  // Close the navigation sheet after navigation.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Route changes and repeat clicks on the active sidebar link both start at the page top.
  useEffect(() => {
    scrollBusinessContentToTop();
  }, [pathname, scrollBusinessContentToTop]);

  // Close the navigation sheet if the viewport switches to desktop while it is open.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleChange = (event) => {
      if (event.matches) setMobileNavOpen(false);
    };

    if (mediaQuery.matches) setMobileNavOpen(false);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  if (shouldRedirectToTrust) {
    return <Navigate to="/business/settings?tab=trust" replace />;
  }

  return (
    <div className="business-portal min-h-screen bg-canvas text-foreground md:h-screen md:overflow-hidden md:[contain:paint]">
      <div className="business-app-frame mx-auto min-h-screen w-full md:h-full">
        {/* Phones: navigation sheet — focus-trapped, Escape and outside tap close it. */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen} title="Business navigation" titleHidden>
          <BusinessSidebar onNavigate={handleSidebarNavigate} onClose={() => setMobileNavOpen(false)} />
        </Sheet>

        <div className="flex min-h-screen w-full md:h-full md:min-h-0">
          <aside className="hidden h-full min-h-0 w-[var(--layout-sidebar)] shrink-0 overflow-hidden border-r border-default bg-surface md:block">
            <BusinessSidebar onNavigate={handleSidebarNavigate} />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col md:h-full md:min-h-0">
            <header className="sticky top-0 z-30 h-[var(--layout-topbar)] shrink-0 border-b border-default bg-surface">
              <div className="flex h-full items-center gap-2 px-2 md:px-6">
                <IconButton icon={MenuIcon} label="Open menu" onClick={() => setMobileNavOpen(true)} className="md:hidden" />
                <div className="h-full min-w-0 flex-1">
                  <BusinessTopbar />
                </div>
              </div>
            </header>

            <main
              ref={contentRef}
              className={`business-content flex min-h-0 min-w-0 flex-1 flex-col px-4 py-5 md:px-6 md:py-6 ${
                isMessagingRoute ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden scrollbar-fade"
              }`}
            >
              <Outlet />
            </main>
          </div>
        </div>
      </div>

      {/* Modal / Portal root (kept in DOM) */}
      <div id="business-swal-root" />
    </div>
  );
}
